import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { countriesOptions, passCheckOptions, yesOrNoOPTIONS, personOptions } from '../../companyStructureFieldsOption';
import { AMOUNT_INPUT_VALUE_TYPE, EXTERNAL_KYC_LINK } from 'components/constants';
import i18nContext from 'components/i18n-context';
import { CORPORATE_ACCOUNT_DETAILS_UPDATE, FULL_ACCESS, hasRole } from 'services/roles';
import { parseExternalKycState } from 'services/utils';
import AmountInput from 'uikit/AmountInput';
import Button from 'uikit/Button/Button';
import { CustomDatePicker } from 'uikit/CustomDatePicker';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './AuthorizedPerson.scss';

const AuthorizedPerson = ({
  propertyPrefix,
  data,
  errors,
  handleChange,
  setFieldValue,
  handleDelete,
  parseFullAddress,
  changeExternalKycReference
}) => {
  const i18n = useContext(i18nContext);
  const isDisabled = !hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE);
  const externalKycState = parseExternalKycState(data.kyc_external_state);
  const [kycExternalReference, setKycExternalReference] = useState(data?.kycExternalReference || '');

  useEffect(() => {
    setKycExternalReference(data?.kycExternalReference || '');
  }, [data?.kycExternalReference]);

  const handleChangeCustomInput = (name, value) => {
    setFieldValue(name, value);
  };

  const handleChangeAmountInput = (e) => {
    setFieldValue(e.target.name, e.target.value);
  };

  return (
    <>
      <tr className={'table-row'}>
        <td colSpan={2} className={'table-cell'}>
          <div className={'table-cell-title'}>
            {i18n.getMessage('companyStructure.authorizedPerson.title')}
            <Button type={'outline'} onClick={handleDelete} isDisabled={isDisabled}>
              {i18n.getMessage('companyStructure.authorizedPerson.button.delete')}
            </Button>
          </div>
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.customerId')}</td>
        <td className={'table-cell'}>
          <span className={'table-cell-span'}>{data.accountNumber}</span>
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.name')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.name'}
            value={data.name}
            onChange={handleChange}
            error={errors?.name}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.surname')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.surname'}
            value={data.surname}
            onChange={handleChange}
            error={errors?.surname}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.email')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.email'}
            value={data.email}
            onChange={handleChange}
            error={errors?.email}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.birthday')}</td>
        <td className={'table-cell'}>
          <CustomDatePicker
            isDisabled={isDisabled}
            name={propertyPrefix + '.birthday'}
            value={data.birthday}
            onChange={handleChangeCustomInput}
            error={errors?.birthday}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.passportNumber')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.passportNumber'}
            value={data.passportNumber}
            onChange={handleChange}
            error={errors?.passportNumber}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.passportIssueCountry')}</td>
        <td className={'table-cell'}>
          <InputDropDownSearch
            className={'table-cell-input-dropdown-search'}
            isDisabled={isDisabled}
            options={countriesOptions}
            name={propertyPrefix + '.passportIssueCountry'}
            value={data.passportIssueCountry}
            onChange={handleChangeCustomInput}
            error={errors?.passportIssueCountry}
          />
        </td>
      </tr>

      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.passportIssueDate')}</td>
        <td className={'table-cell'}>
          <CustomDatePicker
            isDisabled={isDisabled}
            name={propertyPrefix + '.passportIssueDate'}
            value={data.passportIssueDate}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.passportExpiryDate')}</td>
        <td className={'table-cell'}>
          <CustomDatePicker
            isDisabled={isDisabled}
            name={propertyPrefix + '.passportExpiryDate'}
            value={data.passportExpiryDate}
            onChange={handleChangeCustomInput}
            error={errors?.passportExpiryDate}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.fullAddress')}</td>
        <td className={'table-cell'}>
          <div className={'input-with-save-button-wrapper'}>
            <Input
              isDisabled={isDisabled}
              className={'table-cell-input'}
              name={propertyPrefix + '.address.fullAddress'}
              value={data.address.fullAddress}
              error={errors?.address?.fullAddress}
              onChange={handleChange}
            />
            <Button
              className={'company-structure-tab-row-button parse-full-address-button'}
              size={'small'}
              type={'primary'}
              onClick={async () => {
                try {
                  const parsedAddress = await parseFullAddress(data.address.fullAddress);
                  if (parsedAddress) {
                    await setFieldValue(propertyPrefix + '.address.' + 'address1', parsedAddress.address_1 || '');
                    await setFieldValue(propertyPrefix + '.address.' + 'address2', parsedAddress.address_2 || '');
                    await setFieldValue(propertyPrefix + '.address.' + 'zipCode', parsedAddress.zip_code || '');
                    await setFieldValue(propertyPrefix + '.address.' + 'city', parsedAddress.city || '');
                    await setFieldValue(propertyPrefix + '.address.' + 'country', parsedAddress.country || '');
                    await setFieldValue(propertyPrefix + '.address.' + 'fullAddress', parsedAddress.full_address || '');
                  }
                } catch (e) {
                  // error handled in store
                }
              }}
              isDisabled={!hasRole(FULL_ACCESS)}
            >
              {i18n.getMessage('companyStructure.button.parseAddress')}
            </Button>
          </div>
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.address1')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.address.address1'}
            value={data.address.address1}
            error={errors?.address?.address1}
            onChange={handleChange}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.address2')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={true}
            className={'table-cell-input'}
            name={propertyPrefix + '.address.address2'}
            value={data.address.address2}
            error={errors?.address?.address2}
            onChange={handleChange}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.zipCode')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.address.zipCode'}
            value={data.address.zipCode}
            error={errors?.address?.zipCode}
            onChange={handleChange}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.city')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.address.city'}
            value={data.address.city}
            error={errors?.address?.city}
            onChange={handleChange}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.country')}</td>
        <td className={'table-cell'}>
          <InputDropDownSearch
            className={'table-cell-input-dropdown-search'}
            isDisabled={isDisabled}
            options={countriesOptions}
            name={propertyPrefix + '.address.country'}
            value={data.address.country}
            onChange={handleChangeCustomInput}
            error={errors?.address?.country}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.passportCheck')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={passCheckOptions}
            name={propertyPrefix + '.passportCheck'}
            value={data.passportCheck}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.sanctionList')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={passCheckOptions}
            name={propertyPrefix + '.sanctionList'}
            value={data.sanctionList}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.pepList')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={passCheckOptions}
            name={propertyPrefix + '.pepList'}
            value={data.pepList}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.blackList')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={passCheckOptions}
            name={propertyPrefix + '.blackList'}
            value={data.blackList}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.beneficiary')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={yesOrNoOPTIONS}
            name={propertyPrefix + '.beneficiary'}
            value={data.beneficiary}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.director')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={yesOrNoOPTIONS}
            name={propertyPrefix + '.director'}
            value={data.director}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.residenceCountry')}</td>
        <td className={'table-cell'}>
          <InputDropDownSearch
            className={'table-cell-input-dropdown-search'}
            isDisabled={isDisabled}
            options={countriesOptions}
            name={propertyPrefix + '.residenceCountry'}
            value={data.residenceCountry}
            onChange={handleChangeCustomInput}
            error={errors?.residenceCountry}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.memberOfTheBoard')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={yesOrNoOPTIONS}
            name={propertyPrefix + '.memberOfTheBoard'}
            value={data.memberOfTheBoard}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.representative')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={yesOrNoOPTIONS}
            name={propertyPrefix + '.representative'}
            value={data.representative}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.shareholder')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={yesOrNoOPTIONS}
            name={propertyPrefix + '.shareholder'}
            value={data.shareholder}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.percentOfEquity')}</td>
        <td className={'table-cell'}>
          <AmountInput
            className={'table-cell-input'}
            isDisabled={isDisabled}
            name={propertyPrefix + '.percentOfEquity'}
            value={data.percentOfEquity}
            disabled={isDisabled}
            precision={2}
            returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
            error={errors?.percentOfEquity}
            onChange={handleChangeAmountInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.position')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.position'}
            value={data.position}
            onChange={handleChange}
            error={errors?.position}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.title')}</td>
        <td className={'table-cell'}>
          <InputDropDown
            isDisabled={isDisabled}
            className={'table-cell-input'}
            options={personOptions}
            name={propertyPrefix + '.title'}
            value={data.title}
            onChange={handleChangeCustomInput}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.accountOpeningPurpose')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.accountOpeningPurpose'}
            value={data.accountOpeningPurpose}
            onChange={handleChange}
            error={errors?.accountOpeningPurpose}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.accountClosingReason')}</td>
        <td className={'table-cell'}>
          <Input
            isDisabled={isDisabled}
            className={'table-cell-input'}
            name={propertyPrefix + '.accountClosingReason'}
            value={data.accountClosingReason}
            onChange={handleChange}
            error={errors?.accountClosingReason}
          />
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.kycExternalReference')}</td>
        <td className={'table-cell'}>
          <div className={'input-with-save-button-wrapper'}>
            <Input
              isDisabled={isDisabled}
              className={'table-cell-input'}
              name={propertyPrefix + '.kycExternalReference'}
              value={kycExternalReference}
              error={errors?.kycExternalReference}
              onChange={handleChange}
            />
            <Button
              className={'company-structure-tab-row-button'}
              size={'small'}
              type={'primary'}
              onClick={() => changeExternalKycReference(kycExternalReference)}
              isDisabled={!hasRole(CORPORATE_ACCOUNT_DETAILS_UPDATE)}
            >
              {i18n.getMessage('companyStructure.button.save')}
            </Button>
          </div>
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.externalKycLink')}</td>
        <td className={'table-cell'}>
          <span className={'table-cell-span'}>
            <a
              className={'link-normal'}
              href={`${EXTERNAL_KYC_LINK}${externalKycState.identifier}`}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              {`${EXTERNAL_KYC_LINK}${externalKycState.identifier}`}
            </a>
          </span>
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.statuses')}</td>
        <td className={'table-cell'}>
          <span className={'table-cell-span'}>{externalKycState.statuses}</span>
        </td>
      </tr>
      <tr className={'table-row'}>
        <td className={'table-cell'}>{i18n.getMessage('companyStructure.fields.tags')}</td>
        <td className={'table-cell'}>
          <span className={'table-cell-span'}>{externalKycState.tags}</span>
        </td>
      </tr>
    </>
  );
};

AuthorizedPerson.prototype = {
  data: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  parseFullAddress: PropTypes.func.isRequired,
  changeExternalKycReference: PropTypes.func.isRequired
};

export default AuthorizedPerson;
